import React, { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';

import Loader from './Loader';

export const MAX_TABLE_ROWS = 500;

// Browsers can't show HEIC, so those get a download instead.
const SHOWN_IMAGE_TYPES = new Set([
	'image/jpeg',
	'image/png',
	'image/gif',
	'image/webp',
]);

/** RFC 4180 CSV: quoted fields, "" escapes, CRLF or LF. Cells stay as typed. */
export const parseCsv = (text: string): string[][] => {
	const rows: string[][] = [];
	let row: string[] = [];
	let cell = '';
	let quoted = false;
	for (let i = 0; i < text.length; i += 1) {
		const char = text[i];
		if (quoted) {
			if (char === '"' && text[i + 1] === '"') {
				cell += '"';
				i += 1;
			} else if (char === '"') {
				quoted = false;
			} else {
				cell += char;
			}
		} else if (char === '"' && cell === '') {
			// Only a leading quote opens a quoted cell, so 12" pipe stays one cell.
			quoted = true;
		} else if (char === ',') {
			row.push(cell);
			cell = '';
		} else if (char === '\n' || char === '\r') {
			if (char === '\r' && text[i + 1] === '\n') i += 1;
			row.push(cell);
			rows.push(row);
			row = [];
			cell = '';
		} else {
			cell += char;
		}
	}
	if (cell || row.length) rows.push([...row, cell]);
	return rows;
};

interface OriginalFileProps {
	url: string;
	contentType?: string;
	filename?: string;
}

/** The file as the user uploaded it: photos as images, CSV as a table (ROC-91). */
const OriginalFile: React.FC<OriginalFileProps> = ({
	url,
	contentType,
	filename,
}) => {
	const isImage = SHOWN_IMAGE_TYPES.has(contentType ?? '');
	const isCsv = contentType === 'text/csv';
	const [rows, setRows] = useState<string[][] | null>(null);
	const [failed, setFailed] = useState(false);
	// Each poll re-signs the URL; refetch only when the file itself changes.
	const fileUrl = url.split('?')[0];
	const latestUrl = useRef(url);
	latestUrl.current = url;

	useEffect(() => {
		setRows(null);
		setFailed(false);
		if (!isCsv) return;
		let cancelled = false;
		fetch(latestUrl.current)
			.then((response) => {
				if (!response.ok) throw new Error(String(response.status));
				return response.arrayBuffer();
			})
			.then((buffer) => {
				const text = new TextDecoder('utf-8').decode(buffer);
				if (!cancelled) setRows(parseCsv(text));
			})
			.catch(() => {
				if (!cancelled) setFailed(true);
			});
		return () => {
			cancelled = true;
		};
	}, [fileUrl, isCsv]);

	return (
		<div className="flex-1 min-h-0 flex flex-col">
			<div className="flex items-center justify-between p-3 bg-white border-b border-gray-200">
				<span className="text-sm text-gray-600 truncate">
					{filename ?? 'Original file'}
				</span>
				<a
					href={url}
					download={filename ?? true}
					target="_blank"
					rel="noopener noreferrer"
					className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
				>
					<Download className="w-4 h-4 mr-1" />
					Download
				</a>
			</div>
			<div className="flex-1 min-h-0 overflow-auto p-4">
				{isImage && (
					<img
						src={url}
						alt={filename ?? 'Uploaded statement'}
						className="mx-auto max-w-full"
					/>
				)}
				{isCsv && !rows && !failed && (
					<div className="flex items-center justify-center h-full">
						<Loader size="md" text="Loading file..." />
					</div>
				)}
				{isCsv && rows && (
					<>
						{rows.length > MAX_TABLE_ROWS && (
							<p className="mb-2 text-xs text-gray-500">
								Showing {MAX_TABLE_ROWS} of {rows.length} rows. Use Download to
								see all of them.
							</p>
						)}
						<table className="min-w-full text-xs bg-white border border-gray-200">
							<tbody>
								{rows.slice(0, MAX_TABLE_ROWS).map((row, rowIndex) => (
									<tr
										key={rowIndex}
										className={rowIndex === 0 ? 'font-semibold bg-gray-50' : ''}
									>
										{row.map((cell, cellIndex) => (
											<td
												key={cellIndex}
												className="px-2 py-1 border-b border-gray-100 whitespace-nowrap"
											>
												{cell}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</>
				)}
				{((!isImage && !isCsv) || failed) && (
					<p className="text-center text-sm text-gray-500 mt-8">
						This file can't be shown here. Use Download to open it.
					</p>
				)}
			</div>
		</div>
	);
};

export default OriginalFile;
